import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { emptyProfile } from "../data/profileMock";
import { fetchDietitianProfile } from "../services/settingsService";
import type { DietitianProfile } from "../types/settings.types";

/**
 * The same union the other fetching hooks report — `useClients`,
 * `useBrandDetails`. `"failed"` is real here now: the profile comes from
 * `/api/v1/settings/` rather than from a store in this browser, so there is
 * something to be out of reach.
 */
export type ProfileLoadStatus = "loading" | "ready" | "failed";

export interface DietitianProfileState {
  /**
   * The saved profile, once the read has landed — `null` until then. A new
   * identity per load, which is what the page seeds its form state from.
   */
  loaded: DietitianProfile | null;
  status: ProfileLoadStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
}

export const DIETITIAN_PROFILE_QUERY_KEY = ["settings", "profile"] as const;

/**
 * The dietitian's own profile, read when the Settings screen opens.
 *
 * A workspace that has never filled one in is not a failure: the read answers
 * `null` for it and the screen opens on an empty profile, which is the right
 * screen for a first visit.
 *
 * Backed by TanStack Query: a screen visited again shows the last-read
 * profile immediately while a fresh read runs quietly behind it, rather than
 * asking the Settings screen to wait on the network every time it opens.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useDietitianProfile(): DietitianProfileState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: DIETITIAN_PROFILE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const profile = await fetchDietitianProfile(getToken, signal);
      // A fresh object even for a profile that has never been saved, so the
      // page's seeding runs once for it too — see `SettingsPage`.
      return profile ?? { ...emptyProfile };
    },
  });

  useEffect(() => {
    if (query.isError) console.error("profile failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    loaded: query.data ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    reload: () => {
      void query.refetch();
    },
  };
}

export default useDietitianProfile;
